import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkle, Info } from "@phosphor-icons/react";

/** Circular SVG gauge with a sub-score bar rail and trend sparkline. */
function ArcGauge({ score, size = 180 }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (s / 100) * c;
  const color = s >= 80 ? "var(--oz-emerald)" : s >= 60 ? "var(--oz-gold)" : s >= 40 ? "var(--oz-amber)" : "var(--oz-red)";
  const label = s >= 80 ? "Mark-shaped" : s >= 60 ? "Sharpening" : s >= 40 ? "Diverging" : "Generic";

  return (
    <div className="relative flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} stroke="var(--oz-border)" fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          strokeWidth={stroke} stroke={color} fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          style={{ transition: "stroke-dasharray 800ms cubic-bezier(0.16, 1, 0.3, 1), stroke 400ms" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-mono text-4xl tracking-tight" style={{ color }}>{s.toFixed(0)}</div>
        <div className="text-[9px] uppercase tracking-widest text-[color:var(--oz-text-muted)] mt-1">/100</div>
        <div className="text-[10px] uppercase tracking-widest mt-1.5" style={{ color }}>{label}</div>
      </div>
    </div>
  );
}

function Sparkline({ points, width = 260, height = 44 }) {
  const values = points.map((p) => p.score);
  if (!points.length) return null;
  const max = Math.max(100, ...values);
  const min = 0;
  const range = max - min || 1;
  const step = width / Math.max(1, points.length - 1);
  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${i * step},${height - ((p.score - min) / range) * (height - 6) - 3}`)
    .join(" ");

  return (
    <svg width={width} height={height} className="overflow-visible">
      <path d={pathD} fill="none" stroke="var(--oz-emerald)" strokeWidth="1.5" />
      {points.map((p, i) => (
        <circle
          key={i}
          cx={i * step}
          cy={height - ((p.score - min) / range) * (height - 6) - 3}
          r={i === points.length - 1 ? 3 : 1.5}
          fill={i === points.length - 1 ? "var(--oz-gold)" : "var(--oz-emerald)"}
        />
      ))}
      {/* baseline */}
      <line x1="0" x2={width} y1={height - 3} y2={height - 3} stroke="var(--oz-border)" strokeDasharray="2 3" />
    </svg>
  );
}

function SubScoreRow({ label, comp, unit = "%" }) {
  if (!comp) return null;
  const pct = Math.round((comp.accuracy || 0) * 100);
  const bg = pct >= 80 ? "var(--oz-emerald)" : pct >= 60 ? "var(--oz-gold)" : pct >= 40 ? "var(--oz-amber)" : "var(--oz-red)";
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-xs text-[color:var(--oz-text-dim)]">{label}</div>
        <div className="font-mono text-sm" style={{ color: bg }}>
          {comp.has_data ? `${pct}${unit}` : "—"}
        </div>
      </div>
      <div className="prio-bar"><div className="prio-fill" style={{ width: `${pct}%`, background: bg }} /></div>
    </div>
  );
}

export default function CooConfidenceCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/coo/confidence")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="oz-card p-5">
        <div className="text-xs text-[color:var(--oz-text-dim)]">Calibrating…</div>
      </div>
    );
  }
  if (!data) return null;

  const { score, components, trend } = data;
  const hasAnyTrend = trend.some((p) => p.has_data);
  const draft = components.draft, intake = components.intake, rules = components.rules;

  return (
    <div className="oz-card p-5 h-full" data-testid="coo-confidence-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkle size={14} weight="fill" color="var(--oz-gold)" />
          <div className="overline">COO Confidence · 30-day</div>
        </div>
        <span
          className="text-[color:var(--oz-text-muted)] hover:text-[color:var(--oz-text)] cursor-help"
          title="How Mark-shaped is the AI right now? Blend of intake correction rate, draft edit rate, and rule-override rate over the last 30 days."
        >
          <Info size={14} />
        </span>
      </div>

      <div className="flex items-center gap-6">
        <ArcGauge score={score} size={160} />
        <div className="flex-1 min-w-0 space-y-3">
          <SubScoreRow label={`Drafts sent unedited (${draft.total_drafts_sent})`} comp={draft} />
          <SubScoreRow label={`Intakes not corrected (${intake.total_intakes})`} comp={intake} />
          <SubScoreRow label={`Held drafts you approved as-is (${rules.held_drafts})`} comp={rules} />
        </div>
      </div>

      {hasAnyTrend && (
        <div className="mt-5 pt-4 border-t border-[color:var(--oz-border)]">
          <div className="flex items-center justify-between mb-2">
            <div className="overline">8-week trend</div>
            <div className="text-[10px] font-mono text-[color:var(--oz-text-muted)]">
              {trend[0]?.week_start} → today
            </div>
          </div>
          <Sparkline points={trend} />
        </div>
      )}
      {!hasAnyTrend && (
        <div className="mt-4 pt-3 border-t border-[color:var(--oz-border)] text-[11px] text-[color:var(--oz-text-muted)] italic">
          Not enough history yet. Use the intake bar, ship a few relay drafts, and the trendline will start climbing.
        </div>
      )}
    </div>
  );
}
